import { useSelector } from "react-redux";
import { FirebaseUserInfo, logoutUser, retrieveUser, selectUser, userSlice } from "../../redux/user/userSlice";
import { ElementNotAuthenticated } from "./ElementNotAuthenticated";
import ElementSkeleton from "./ElementSkeleton";
import { useEffect } from "react";
import { isTokenReady } from "../user/UserAvatar";
import { assertThumbnailUrl, LoadState } from "../../redux/common";
import { useAppDispatch } from "../../app/hooks";
import { onAuthStateChanged } from "firebase/auth";
import { auth, messaging } from "../../firebase/FirebaseConfig";
import { ElementForbidden } from "./ElementForbidden";
import { getToken, onMessage } from "firebase/messaging";
import { retrieveConversation } from "../../redux/conversation/conversationSlice";
import { retrieveMessageNewers } from "../../redux/conversation/messageListSlice";

interface CheckNotAuthenticatedProps {
    content: () => React.ReactNode;
    notAuthenticated?: () => React.ReactNode;
    forbidden?: () => React.ReactNode;
    loading?: () => React.ReactNode;
}
const CheckNotAuthenticated = ({ content, notAuthenticated, forbidden, loading }: CheckNotAuthenticatedProps) => {
    const user = useSelector(selectUser);
    const dispatch = useAppDispatch();

    useEffect(() => {
        onAuthStateChanged(auth, (newUser) => {
            if (newUser) {
                navigator.serviceWorker.register(process.env.REACT_APP_FIREBASE_SW_FILE || process.env.PUBLIC_URL + "/firebase-messaging-sw.js")
                .then((registration) => {
                    console.debug(`Service worker registered: ${registration.scope}`);
                    getToken(messaging, {
                        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
                        serviceWorkerRegistration: registration,
                    }).then((currentToken) => {
                        if (currentToken) {
                            // console.debug(`Messaging token refreshed: ${currentToken}`);
                            dispatch(userSlice.actions.setMessageToken(currentToken));
                        }
                    }).catch((error) => {
                        console.error(`Messaging token refresh error: ${error}`);
                    });
                }).catch((error) => {
                    console.error(`Service worker registration failed: ${error}`);
                });

                if ((!user.firebase) || (user.firebase.email != newUser.email)) {
                    console.debug("Setting the anchor to null");
                    auth.currentUser?.getIdToken(true)
                        .then((tk) => {
                            console.debug("Token refreshed");
                            dispatch(userSlice.actions.setToken(tk));
                        })
                        .catch((error) => {
                            console.error(`Token refresh error: ${error}`);
                        });
                    setInterval(() => {
                        console.debug(`Refreshing token for ${auth.currentUser?.email}`);
                        auth.currentUser?.getIdToken(true)
                            .then((tk) => {
                                console.debug("Token refreshed");
                                dispatch(userSlice.actions.setToken(tk));
                            })
                            .catch((error) => {
                                console.error(`Token refresh error: ${error}`);
                            });
                    }, 1000 * 60);
                    console.info(`User logged in ${newUser.uid}`);
                    const info: FirebaseUserInfo = {
                        displayName: newUser.displayName,
                        email: newUser.email,
                        phoneNumber: newUser.phoneNumber,
                        photoURL: assertThumbnailUrl(newUser.photoURL),
                        providerId: newUser.providerId,
                        uid: newUser.uid,
                        emailVerified: newUser.emailVerified,
                        isAnonymous: newUser.isAnonymous,
                        refreshToken: newUser.refreshToken,
                        tenantId: newUser.tenantId,
                    };
                    console.log(`User info: ${JSON.stringify(info)}`);
                    dispatch(userSlice.actions.ready(info));
                    console.debug("Setting the anchor to null");
                } else {
                    console.debug("Already logged in");
                }
            } else {
                console.info("User logged out");
                dispatch(logoutUser({}));
                dispatch(userSlice.actions.notReady());
            }
        });

    }, []);

    useEffect(() => {
        if(!user.firebase) {
            console.debug("No firebase user");
            return;
        }
        if(!isTokenReady(user)) {
            console.debug("Token not ready");
            return;
        }
        if (user.priza.state.state != LoadState.NotStarted) {
            console.log(`User already retrieved: ${user.priza.state.state}`);
            return;
        }
        dispatch(retrieveUser({}));
    }, [user]);

    onMessage(messaging, (payload) => {
        console.log("Message received. ", payload);
        if(payload.data != null) {
            const data = payload.data;
            if(data.conversationId != null) {
                dispatch(retrieveConversation({ conversationId: data.conversationId }));
                dispatch(retrieveMessageNewers({ conversationId: data.conversationId, }));
            }
        }
    });

    if (user.state == "in" && user.priza.info) {
        return content();
    } else if (user.state == "off") {
        return notAuthenticated ? notAuthenticated() : <ElementNotAuthenticated />;
    } else if ( user.priza.state.state == LoadState.Failed) {
        console.debug("user", user.priza.state);
        return forbidden ? forbidden() : <ElementForbidden />;
    } else {
        console.debug(`user ${user.state}`);
        return loading ? loading() : <ElementSkeleton />;
    }
};

export default CheckNotAuthenticated;